import { TextInfoGroupAlt } from "@/components/shared";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StandalonePriceDetails } from "@/graphql/__generated__/graphql";
import { formatNumberLocale } from "@/utils/helpers";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";

interface StandalonePriceDetailsCardProps {
  title: string;
  description: string;
  details?: StandalonePriceDetails;
}

/**
 * StandalonePriceDetailsCard - Compact card for displaying tender-level standalone pricing summaries
 * (e.g. standalone cars / standalone purchases).
 *
 * @component
 * @example
 * <StandalonePriceDetailsCard title="Standalone cars" details={subject.standaloneCars} />
 */
function StandalonePriceDetailsCard({ title, description, details }: StandalonePriceDetailsCardProps) {
  const intl = useIntl();
  const { t } = useTranslation(["translation"]);

  if (!details) return null;

  const metrics = [
    {
      label: t("common:pricing.labels.costs"),
      value: formatNumberLocale(intl, details.costs ?? 0, "currency"),
    },
    {
      label: t("common:pricing.labels.revenue"),
      value: formatNumberLocale(intl, details.revenue ?? 0, "currency"),
    },
    {
      label: t("common:pricing.labels.priceperhour"),
      value: formatNumberLocale(intl, details.pricePerHour ?? 0, "currency"),
    },
  ];

  return (
    <Card className="shadow-none p-4">
      <CardHeader className="p-0 pb-4">
        <CardTitle className="text-sm font-bold">{title}</CardTitle>
        <CardDescription className="text-xs text-muted-foreground">{description}</CardDescription>
      </CardHeader>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
        {metrics.map((metric) => (
          <TextInfoGroupAlt
            key={metric.label}
            label={metric.label}
            content={metric.value}
            hasBackground={true}
            backgroundColor="slate"
          />
        ))}
      </div>
    </Card>
  );
}

export default StandalonePriceDetailsCard;
